import { useEffect } from 'react';

/**
 * Custom hook to trap focus within a set of elements.
 * 
 * @param {boolean} isOpen - Whether the focus trap is active.
 * @param {function} onClose - Callback for the Escape key.
 * @param {React.RefObject} firstRef - Ref to the first focusable element.
 * @param {React.RefObject} lastRef - Ref to the last focusable element.
 * @param {React.RefObject} defaultFocusRef - (Optional) Ref to the element to focus initially.
 */
const useFocusTrap = (isOpen, onClose, firstRef, lastRef, defaultFocusRef) => {
  useEffect(() => {
    if (isOpen) {
      // Small timeout to ensure DOM is ready and focus can be applied
      const focusTimeout = setTimeout(() => {
        const elementToFocus = defaultFocusRef?.current || firstRef.current;
        if (elementToFocus) {
          elementToFocus.focus();
        }
      }, 10);

      const isTextEntryElement = (el) => {
        if (!el) return false;
        const tagName = el.tagName.toLowerCase();
        if (tagName === 'textarea') return true;
        if (tagName === 'input') {
          const type = el.type ? el.type.toLowerCase() : 'text';
          const textTypes = ['text', 'password', 'number', 'email', 'search', 'tel', 'url', 'range'];
          return textTypes.includes(type);
        }
        return el.isContentEditable;
      };

      const getCommonNameAncestor = (el1, el2) => {
        if (!el1 || !el2) return null;
        let parent = el1.parentNode;
        while (parent) {
          if (parent.contains(el2)) {
            return parent;
          }
          parent = parent.parentNode;
        }
        return null;
      };

      const FOCUSABLE_SELECTOR = 'a[href], area[href], input:not([disabled]), select:not([disabled]), textarea:not([disabled]), button:not([disabled]), iframe, object, embed, [tabindex="0"], [contenteditable]';

      const getFocusableElements = (container) => {
        if (!container) return [];
        return Array.from(container.querySelectorAll(FOCUSABLE_SELECTOR))
          .filter(el => {
            const style = window.getComputedStyle(el);
            return style.display !== 'none' && style.visibility !== 'hidden' && el.offsetWidth > 0 && el.offsetHeight > 0;
          });
      };

      const handleKeyDown = (e) => {
        if (e.key === 'Tab') {
          if (e.shiftKey) {
            // Shift + Tab: If on first element, move to last
            if (document.activeElement === firstRef.current) {
              e.preventDefault();
              lastRef.current?.focus();
            }
          } else {
            // Tab: If on last element, move to first
            if (document.activeElement === lastRef.current) {
              e.preventDefault();
              firstRef.current?.focus();
            }
          }
        } else if (e.key === 'ArrowLeft' || e.key === 'ArrowRight') {
          if (isTextEntryElement(document.activeElement)) {
            return;
          }

          const ancestor = getCommonNameAncestor(firstRef.current, lastRef.current);
          if (!ancestor) return;

          const focusables = getFocusableElements(ancestor);
          if (focusables.length === 0) return;

          const currentIndex = focusables.indexOf(document.activeElement);
          e.preventDefault();

          if (e.key === 'ArrowLeft') {
            const prevIndex = currentIndex <= 0 ? focusables.length - 1 : currentIndex - 1;
            focusables[prevIndex]?.focus();
          } else {
            const nextIndex = currentIndex === -1 || currentIndex >= focusables.length - 1 ? 0 : currentIndex + 1;
            focusables[nextIndex]?.focus();
          }
        } else if (e.key === 'Escape') {
          if (onClose) onClose();
        }
      };

      window.addEventListener('keydown', handleKeyDown);
      return () => {
        window.removeEventListener('keydown', handleKeyDown);
        clearTimeout(focusTimeout);
      };
    }
  }, [isOpen, onClose, firstRef, lastRef]);
};

export default useFocusTrap;
